import {
  parseQrPayload, projectCarts, resolveActiveRound, resolveOpenCorrection, summariseRounds,
  otherOpenRoundsAtSite, stalledRounds, roundProgress, canReopen, canRetract,
  type Cart, type CartState, type ClientEvent, type RoundProgress, type RoundSummary, type Site,
} from '@chariot/contract';
import { db, getMeta, setMeta, requestPersistence, type LocalRejection } from './db.ts';
import { deviceId } from './device.ts';
import { api, ApiError, type Identity } from './api.ts';
import { pendingCount, syncOnce, type SyncOutcome } from './sync.ts';
import type { ScanOutcome } from './scan/outcome.ts';

export type { ScanOutcome };


/**
 * Application state.
 *
 * Loaded entirely from IndexedDB and projected in memory. At ~200 carts and a few thousand
 * events the projection is recomputed outright on every change rather than materialised,
 * which removes any possibility of a cached view disagreeing with the log.
 */
class AppStore {
  booted = $state(false);
  /**
   * `navigator.onLine` only says whether a network interface exists — it is true on a wifi
   * that goes nowhere, and true when the server itself is down. So reachability is tracked
   * from what actually happened on the last sync, and the two are combined for display.
   */
  online = $state(typeof navigator === 'undefined' ? true : navigator.onLine);
  serverReachable = $state(true);
  identity = $state<Identity | null>(null);
  authChecked = $state(false);

  sites = $state<Site[]>([]);
  carts = $state<Cart[]>([]);
  events = $state<ClientEvent[]>([]);
  rejections = $state<LocalRejection[]>([]);

  device = $state('');
  pending = $state(0);
  syncing = $state(false);
  lastSync = $state<string | null>(null);
  lastSyncError = $state<string | null>(null);
  staleAfterDays = $state(30);
  persisted = $state(false);

  /** Forces the WASM scan path in a Chromium browser, so the fallback is actually testable. */
  forceFallbackScanner = $state(false);

  /**
   * How long the scanner holds a result on screen before taking the next cart, and whether
   * each scan is also announced with a sound. Both are per-device preferences: a decode is
   * far faster than a human can read one, and how much of a pause that needs depends on the
   * operator and on the building.
   */
  scanHoldMs = $state(1800);
  scanSound = $state(true);

  states = $derived(
    projectCarts(this.carts, this.events, { staleAfterDays: this.staleAfterDays }),
  );
  activeRound = $derived(resolveActiveRound(this.events, this.device));
  /**
   * A correction being recorded right now. Held separately from `activeRound` so the
   * Contrôle screen never mistakes "I found one cart in a corner" for a sweep in progress,
   * and read back from the log so a killed app resumes the same one.
   */
  correction = $derived(resolveOpenCorrection(this.events, this.device));
  /** Where a scan goes right now: into the correction being recorded, else into the sweep. */
  scanTarget = $derived(this.correction ?? this.activeRound);
  rounds = $derived(
    [...summariseRounds(this.events).values()].sort((a, b) => b.opened_at.localeCompare(a.opened_at)),
  );
  isAdmin = $derived(this.identity?.roles.includes('admin') ?? false);
  /** What the operator should be told: connected, or working from the device. */
  connected = $derived(this.online && this.serverReachable);

  siteByCode = $derived(new Map(this.sites.map((s) => [s.code, s])));
  cartByCode = $derived(new Map(this.carts.map((c) => [c.code, c])));
  roundsById = $derived(new Map(this.rounds.map((r) => [r.round_id, r])));

  staleCarts = $derived([...this.states.values()].filter((s) => s.stale));
  missingCarts = $derived([...this.states.values()].filter((s) => s.missing_since));

  // ------------------------------------------------------------------ lifecycle

  async boot() {
    this.device = await deviceId();
    this.staleAfterDays = await getMeta<number>('stale_after_days', 30);
    this.scanHoldMs = await getMeta<number>('scan_hold_ms', 1800);
    this.scanSound = await getMeta<boolean>('scan_sound', true);
    this.lastSync = await getMeta<string | null>('last_sync', null);
    this.persisted = await requestPersistence();
    await this.reloadLocal();

    // The identity from the last successful sign-in. The app is unusable without one, but
    // it is read locally first so a cold start in a dead zone still opens for someone who
    // has signed in before — the alternative would strand an operator mid-round.
    this.identity = await getMeta<Identity | null>('identity', null);

    try {
      const live = await api.me();
      await this.setIdentity(live);
    } catch (e) {
      if (e instanceof ApiError && e.unauthenticated) {
        // The server was reachable and disowned us: the session is genuinely gone, so the
        // cached identity must not keep the door open.
        await this.setIdentity(null);
      }
      // Any other failure means the server could not be reached. The cached identity stands.
    }
    this.authChecked = true;
    this.booted = true;

    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        this.online = true;
        void this.sync();
      });
      window.addEventListener('offline', () => (this.online = false));
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void this.sync();
      });
      setInterval(() => void this.sync(), 60_000);
    }
    void this.sync();
  }

  /** Single path for identity changes, so the persisted copy never drifts from memory. */
  async setIdentity(who: Identity | null) {
    this.identity = who;
    await setMeta('identity', who);
  }

  async setScanHold(ms: number) {
    this.scanHoldMs = ms;
    await setMeta('scan_hold_ms', ms);
  }

  async setScanSound(on: boolean) {
    this.scanSound = on;
    await setMeta('scan_sound', on);
  }

  async reloadLocal() {
    const [sites, carts, rows, rejections] = await Promise.all([
      db.sites.toArray(),
      db.carts.toArray(),
      db.events.toArray(),
      db.rejections.orderBy('seen_at').reverse().limit(100).toArray(),
    ]);
    this.sites = sites.sort((a, b) => a.code.localeCompare(b.code));
    this.carts = carts.sort((a, b) => a.code.localeCompare(b.code));
    this.events = rows.map((r) => r.event);
    this.rejections = rejections;
    this.pending = await pendingCount();
  }

  async sync(): Promise<SyncOutcome> {
    if (this.syncing) return { ok: false, reason: 'error', detail: 'already syncing' };
    this.syncing = true;
    try {
      const out = await syncOnce();
      if (out.ok) {
        this.lastSyncError = null;
        this.serverReachable = true;
        await this.reloadLocal();
        this.lastSync = await getMeta<string | null>('last_sync', null);
        if (!this.identity) {
          try {
            await this.setIdentity(await api.me());
          } catch {
            /* the sync itself succeeded; identity refresh can wait */
          }
        }
      } else if (out.reason === 'offline') {
        // Normal, expected, and already communicated by the pending counter. Not an error.
        this.serverReachable = false;
        this.lastSyncError = null;
      } else if (out.reason === 'unauthenticated') {
        // Only call it an expiry if there was actually a session to expire. On a first run
        // the "not signed in" banner already says everything, and a scarier second message
        // would just look like a fault.
        const hadSession = this.identity !== null;
        await this.setIdentity(null);
        this.lastSyncError = hadSession
          ? 'Session expirée — reconnectez-vous pour continuer.'
          : null;
      } else if (out.reason === 'error') {
        this.lastSyncError = out.detail ?? 'Échec de synchronisation';
      }
      return out;
    } finally {
      this.syncing = false;
    }
  }

  // ------------------------------------------------------------------ event append

  /**
   * Durably record an event, then reflect it. The write is awaited before the caller can
   * report success, so nothing the operator sees confirmed exists only in memory.
   */
  private async append(
    build: (base: { id: string; at: string; device_id: string; user_id: string }) => ClientEvent,
  ): Promise<ClientEvent> {
    const ev = build({
      id: crypto.randomUUID(),
      at: new Date().toISOString(),
      device_id: this.device,
      // Attribution from the cached identity, so it survives days offline. The server
      // re-stamps it from the authenticated session on arrival.
      user_id: this.identity?.user_id ?? 'offline:unknown',
    });
    await db.events.add({
      id: ev.id,
      at: ev.at,
      sync_state: 'pending',
      round_id: ev.round_id,
      cart_code: ev.type === 'cart_scanned' ? ev.cart_code : null,
      event: ev,
    });
    this.events = [...this.events, ev];
    this.pending += 1;
    void this.sync();
    return ev;
  }

  // ------------------------------------------------------------------ rounds

  otherRoundsHere(site_code: string): RoundSummary[] {
    return otherOpenRoundsAtSite(this.events, site_code, this.device);
  }

  async openRound(site_code: string): Promise<ScanOutcome> {
    const site = this.siteByCode.get(site_code);
    if (!site) return { kind: 'unknown_site', code: site_code };
    // Opening a second round on one device would orphan the first, leaving it open for
    // ever and making this device look to everyone else like a permanent concurrent sweep.
    const active = this.activeRound;
    if (active) {
      return active.site_code === site_code
        ? { kind: 'round_opened', site }
        : { kind: 'other_site', site: this.siteByCode.get(active.site_code) ?? site };
    }
    const round_id = crypto.randomUUID();
    await this.append((b) => ({ ...b, type: 'round_opened', round_id, site_code }));
    return { kind: 'round_opened', site };
  }

  /**
   * Record carts found outside a sweep — the one left in a corner, noticed after the round
   * was closed.
   *
   * It is a real round in the log, because a scan belongs to one; what makes it different
   * is that it concludes nothing. Re-scanning the whole site to correct a single cart would
   * be the only alternative, and an operator who has to do that will simply not correct it.
   */
  async beginCorrection(site_code: string): Promise<ScanOutcome> {
    const site = this.siteByCode.get(site_code);
    if (!site) return { kind: 'unknown_site', code: site_code };
    // Always start a fresh one, ending anything left open — an app killed mid-correction
    // would otherwise reappear days later with somebody's earlier cart already in the list.
    // Its scans were durable from the moment they were made; only the opening is tidied up.
    await this.endCorrection();
    const round_id = crypto.randomUUID();
    await this.append((b) => ({
      ...b, type: 'round_opened', round_id, site_code, mode: 'correction',
    }));
    return { kind: 'round_opened', site };
  }

  /** Ends it the way an incomplete sweep ends: the scans stand, nothing is concluded. */
  async endCorrection() {
    const c = this.correction;
    if (!c) return;
    await this.append((b) => ({
      ...b, type: 'round_abandoned', round_id: c.round_id, site_code: c.site_code,
      reason: 'correction',
    }));
  }

  /** Progress of the round in progress, computed on the device so it works with no network. */
  progressForActiveRound(): RoundProgress | null {
    const r = this.activeRound;
    if (!r) return null;
    return roundProgress(this.carts, this.events, r, { staleAfterDays: this.staleAfterDays });
  }

  /** Same computation for a round already over — what it found, not what is current. */
  progressForRound(round: RoundSummary): RoundProgress {
    return roundProgress(this.carts, this.events, round, { staleAfterDays: this.staleAfterDays });
  }

  /** Every round recorded at a site, newest first. */
  roundsAtSite(site_code: string): RoundSummary[] {
    return this.rounds.filter((r) => r.site_code === site_code);
  }

  /** The scans of one round in the order they were made — the sweep as it happened. */
  scansInRound(round_id: string) {
    return this.events
      .filter((e) => e.type === 'cart_scanned' && e.round_id === round_id)
      .sort((a, b) => a.at.localeCompare(b.at));
  }

  async closeRound(): Promise<RoundProgress | null> {
    const r = this.activeRound;
    if (!r) return null;
    // The same snapshot the operator has been watching, so the closing figures match the
    // ones they saw count down.
    const p = roundProgress(this.carts, this.events, r, { staleAfterDays: this.staleAfterDays });
    await this.append((b) => ({
      ...b,
      type: 'round_closed',
      round_id: r.round_id,
      site_code: r.site_code,
      missing: p.missing,
      expected_count: p.expected.length,
      scanned_count: p.scanned.length,
    }));
    return p;
  }

  /**
   * Take a finished round up again.
   *
   * Nothing is edited: the closure is *withdrawn* by appending to the log, so the round
   * accepts scans again and its next closure states the conclusion afresh. Two phones that
   * both reopen still produce two facts that merge.
   */
  async reopenRound(round_id: string): Promise<'ok' | 'too_old' | 'busy' | 'unknown'> {
    const r = this.roundsById.get(round_id);
    if (!r) return 'unknown';
    if (!canReopen(r)) return 'too_old';
    // Two open sweeps on one device would orphan one of them.
    if (this.activeRound && this.activeRound.round_id !== round_id) return 'busy';
    await this.append((b) => ({
      ...b, type: 'round_reopened', round_id: r.round_id, site_code: r.site_code,
    }));
    return 'ok';
  }

  /** Withdraw one scan — the wrong cart was read, or one was counted twice. */
  async retractScan(scan: { id: string; at: string; round_id: string; cart_code: string; site_code: string }) {
    if (!canRetract(scan.at)) return 'too_old' as const;
    await this.append((b) => ({
      ...b,
      type: 'scan_retracted',
      round_id: scan.round_id,
      scan_id: scan.id,
      cart_code: scan.cart_code,
      site_code: scan.site_code,
      reason: 'mis_scan',
    }));
    return 'ok' as const;
  }

  async abandonRound(reason: 'interrupted' | 'wrong_site' | 'other' = 'interrupted') {
    const r = this.activeRound;
    if (!r) return;
    await this.append((b) => ({
      ...b,
      type: 'round_abandoned',
      round_id: r.round_id,
      site_code: r.site_code,
      reason,
    }));
  }

  // ------------------------------------------------------------------ scanning

  /** Interprets any scanned payload in the context of the round in progress. */
  async handleScan(raw: string): Promise<ScanOutcome> {
    const parsed = parseQrPayload(raw);
    if (!parsed.ok) {
      if (parsed.reason === 'unknown_version') {
        return { kind: 'unknown_version', detail: parsed.detail ?? '' };
      }
      if (parsed.reason === 'legacy_unversioned') {
        return { kind: 'legacy_label', detail: parsed.detail ?? '' };
      }
      return { kind: 'malformed', detail: parsed.detail ?? '' };
    }

    // A site QR is still recognised — a stray label may exist — but it no longer starts a
    // round. With a handful of stable sites, picking from a list beats a camera round-trip.
    if (parsed.kind === 'site') {
      return { kind: 'site_label', code: parsed.code, name: this.siteName(parsed.code) };
    }

    const active = this.scanTarget;
    if (!active) return { kind: 'no_round', code: parsed.code };

    const cart = this.cartByCode.get(parsed.code);
    if (!cart) return { kind: 'unknown_cart', code: parsed.code };
    if (active.scanned.includes(cart.code)) return { kind: 'cart_duplicate', cart };

    const state = this.states.get(cart.code);
    const before = state?.current_site ?? null;
    const wasMissing = state?.missing_since != null;
    await this.append((b) => ({
      ...b,
      type: 'cart_scanned',
      round_id: active.round_id,
      cart_code: cart.code,
      site_code: active.site_code,
    }));

    if (cart.status !== 'active') return { kind: 'cart_written_off', cart };
    return {
      kind: 'cart_recorded',
      cart,
      movedFrom: before && before !== active.site_code ? before : null,
      firstPlacement: before === null,
      clearedMissing: wasMissing,
    };
  }

  // ------------------------------------------------------------------ catalogue (online)

  async createSite(name: string) {
    const s = await api.createSite(name);
    await db.sites.put(s);
    await this.reloadLocal();
    return s;
  }
  async createCart(name: string, site_code?: string | null) {
    const c = await api.createCart(name);
    await db.carts.put(c);
    await this.reloadLocal();
    if (site_code) await this.placeCarts([c.code], site_code);
    return c;
  }
  async createCartsBulk(count: number, prefix: string, site_code?: string | null) {
    const list = await api.createCartsBulk(count, prefix);
    await db.carts.bulkPut(list);
    await this.reloadLocal();
    if (site_code) await this.placeCarts(list.map((c) => c.code), site_code);
    return list;
  }

  /**
   * Record carts as standing at a site without anybody walking a sweep.
   *
   * A cart's position is derived from its scans, so "created at Entrepôt Nord" cannot be a
   * column on the cart — it has to be the fact that they were seen there. That fact is
   * written the same way a cart found in a corner is: a round in `correction` mode, which
   * concludes nothing about the carts nobody looked at.
   *
   * It is an assertion made from the office rather than a camera read, and that is the
   * honest reading of registering twenty carts that are standing in front of you. A real
   * scan later simply confirms it; a scan elsewhere moves them, as for any other cart.
   */
  async placeCarts(codes: readonly string[], site_code: string) {
    if (codes.length === 0) return;
    const opened = await this.beginCorrection(site_code);
    if (opened.kind !== 'round_opened') return;
    const round = this.correction;
    if (!round) return;
    for (const cart_code of codes) {
      await this.append((b) => ({
        ...b, type: 'cart_scanned', round_id: round.round_id, cart_code, site_code,
      }));
    }
    await this.endCorrection();
  }
  async setCartStatus(code: string, status: Cart['status']) {
    const c = await api.updateCart(code, { status });
    await db.carts.put(c);
    await this.reloadLocal();
  }
  async setSiteStatus(code: string, status: Site['status']) {
    const s = await api.updateSite(code, { status });
    await db.sites.put(s);
    await this.reloadLocal();
  }
  async renameCart(code: string, name: string) {
    const c = await api.updateCart(code, { name });
    await db.carts.put(c);
    await this.reloadLocal();
  }
  async renameSite(code: string, name: string) {
    const s = await api.updateSite(code, { name });
    await db.sites.put(s);
    await this.reloadLocal();
  }

  /** Site name for display, falling back to the code if the catalogue lacks it (a site
   *  can legitimately be referenced by an event before a sync has fetched it). */
  siteName(code: string | null): string {
    if (!code) return '—';
    return this.siteByCode.get(code)?.name ?? code;
  }

  /** Openings nobody ever terminated — surfaced so they can be chased rather than lingering. */
  get stalled(): RoundSummary[] {
    return stalledRounds(this.events);
  }

  cartsAt(site_code: string): CartState[] {
    return [...this.states.values()]
      .filter((s) => s.status === 'active' && s.current_site === site_code)
      .sort((a, b) => a.code.localeCompare(b.code));
  }
}

export const store = new AppStore();
